import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErr, fmtL, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { ScalesIcon, ArrowCounterClockwiseIcon, MagnifyingGlassIcon, CheckCircleIcon } from "@phosphor-icons/react";

const ACCION = {
  devolver_saldo: "Devolver la deuda al estado de cuenta",
  separar_historica: "Dejar la histórica como detalle (sin saldo)",
  crear_estado_cuenta: "Crear estado de cuenta con la deuda declarada",
  retirar_indebido: "Retirar saldo indebido (carga multi-línea)",
  limpiar_residuo: "Limpiar saldo residual (no contaba en CxC)",
  relacionar: "Relacionar detalle y estado de cuenta",
  marcar_historica: "Marcar como historial comercial",
  excluir_venta_duplicada: "No contar dos veces la venta",
};

function Stat({ label, value, tone = "" }) {
  return (
    <div className="bg-app border border-border rounded-md p-3">
      <div className="text-[11px] text-text-muted">{label}</div>
      <div className={`text-lg font-heading font-semibold ${tone}`}>{value}</div>
    </div>
  );
}

/** Historial comercial vs. cuentas por cobrar + revisión trazable de cargas anteriores (simular → aplicar → revertir). */
export default function HistoricalReconcile({ isAdmin, refreshKey = 0 }) {
  const [summary, setSummary] = useState(null);
  const [plan, setPlan] = useState(null);
  const [batches, setBatches] = useState([]);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const loadSummary = () => api.get("/excel-imports/historical-summary").then(({ data }) => setSummary(data)).catch(() => {});
  const loadBatches = () => isAdmin && api.get("/excel-imports/historical-reconcile/batches")
    .then(({ data }) => setBatches(data || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSummary(); loadBatches(); }, [refreshKey, isAdmin]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const dryRun = () => run(async () => {
    const { data } = await api.post("/excel-imports/historical-reconcile/dry-run");
    setPlan(data); setConfirm("");
  });
  const apply = () => run(async () => {
    const { data } = await api.post("/excel-imports/historical-reconcile/apply", { plan_hash: plan.plan_hash, confirmar: confirm });
    toast.success(`Corrección aplicada: ${data.cambios} documentos. CxC = ${fmtL(data.cxc_real_despues)}`);
    setPlan(null); setConfirm(""); loadSummary(); loadBatches();
  });
  const revert = (id) => {
    if (!window.confirm("¿Revertir este lote? Se restaurará el estado previo de cada documento.")) return;
    run(async () => {
      const { data } = await api.post(`/excel-imports/historical-reconcile/batches/${id}/revert`);
      toast.success(`Lote revertido (${data.revertidas} documentos)`);
      loadSummary(); loadBatches();
    });
  };

  const t = plan?.totales;
  const palabra = plan?.confirmacion || "SEPARAR";
  return (
    <div className="mt-5 bg-card border border-border/60 rounded-lg p-5" data-testid="historical-reconcile">
      <div className="flex items-center gap-2 mb-3">
        <ScalesIcon size={18} className="text-agro-green" />
        <h3 className="font-heading font-semibold">Historial comercial y cuentas por cobrar</h3>
      </div>
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2" data-testid="hist-summary">
          <Stat label="Facturas del historial (sin efecto financiero)" value={summary.ventas_historicas_solo_estadistica} />
          <Stat label="Relacionadas con su estado de cuenta" value={summary.historicas_relacionadas_con_estado_cuenta ?? 0} tone="text-agro-green" />
          <Stat label="Estados de cuenta con saldo" value={`${summary.saldos_apertura_vigentes} · ${fmtL(summary.saldo_apertura_vigente)}`} />
          <Stat label="Históricas con efecto financiero (revisar)" value={`${summary.historicas_con_efecto_financiero ?? 0} · ${fmtL(summary.saldo_historicas_con_efecto_financiero)}`}
            tone={summary.historicas_con_efecto_financiero > 0 ? "text-agro-yellow" : ""} />
        </div>
      )}
      <p className="text-xs text-text-muted mb-3">
        El saldo por cobrar vive solo en los <strong>Estados de Cuenta</strong>. Las <strong>Facturas Históricas (multi-línea)</strong> aportan el detalle
        para reportes y seguimiento comercial, sin saldos ni movimientos contables o de inventario.
      </p>

      {isAdmin && (
        <div className="border-t border-border pt-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <h4 className="font-medium text-sm mr-auto">Revisión de facturas históricas importadas</h4>
            <button onClick={dryRun} disabled={busy} data-testid="reconcile-dry-run"
              className="bg-app border border-border hover:border-agro-green/50 rounded-md px-3 py-1.5 text-sm flex items-center gap-2 disabled:opacity-40">
              <MagnifyingGlassIcon size={14} /> 1. Simular (no modifica nada)
            </button>
          </div>
          {plan && (
            <div className="space-y-3" data-testid="reconcile-plan">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="CxC actual" value={fmtL(t.cxc_antes)} />
                <Stat label="Saldo indebido a retirar" value={fmtL(t.indebido_retirado)} tone={t.indebido_retirado > 0 ? "text-agro-yellow" : ""} />
                <Stat label="CxC después (proyectado)" value={fmtL(t.cxc_despues)} />
                <Stat label="Obligaciones legítimas" value={t.cuadra ? "Intactas" : "No cuadra"} tone={t.cuadra ? "text-agro-green" : "text-red-400"} />
              </div>
              <div className="text-xs text-text-secondary flex flex-wrap gap-x-3">
                {Object.entries(ACCION).map(([k, l]) => plan.resumen[k] ? <span key={k}>{l}: <b>{plan.resumen[k]}</b></span> : null)}
                <span>Revisión manual: <b>{plan.resumen.revision_manual}</b></span>
              </div>
              {plan.impacto_por_cliente?.length > 0 && (
                <div className="border border-agro-yellow/30 bg-agro-yellow/5 rounded-md p-3 text-xs" data-testid="reconcile-impact">
                  <div className="font-medium mb-1">Impacto en saldos por cliente (efectos indebidos que se retiran)</div>
                  <ul className="space-y-0.5 max-h-40 overflow-auto">{plan.impacto_por_cliente.map((x) => (
                    <li key={x.cliente_id}>{x.cliente_nombre || x.cliente_id}: {fmtL(x.cxc_antes)} → {fmtL(x.cxc_despues)} (retira {fmtL(x.retirado)})</li>
                  ))}</ul>
                </div>
              )}
              {plan.cambios_preview.length > 0 && (
                <div className="max-h-64 overflow-auto border border-border rounded-md">
                  <table className="w-full text-xs">
                    <thead className="bg-app sticky top-0"><tr className="text-left text-text-muted">
                      <th className="p-2">Número</th><th className="p-2">Cliente</th><th className="p-2">Fecha</th><th className="p-2">Documento</th>
                      <th className="p-2">Acción</th><th className="p-2 text-right">Saldo antes</th><th className="p-2 text-right">Saldo después</th>
                    </tr></thead>
                    <tbody>{plan.cambios_preview.map((c) => (
                      <tr key={c.id} className="border-t border-border/50">
                        <td className="p-2 font-mono">{c.numero}</td><td className="p-2">{c.cliente_nombre}</td><td className="p-2">{c.fecha}</td>
                        <td className="p-2">{c.documento}</td><td className="p-2">{ACCION[c.accion] || c.accion}</td>
                        <td className="p-2 text-right">{fmtL(c.saldo_antes)}</td><td className="p-2 text-right">{fmtL(c.saldo_despues)}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
              )}
              {plan.no_conciliadas.length > 0 && (
                <div className="border border-orange-500/30 bg-orange-500/5 rounded-md p-3 text-xs" data-testid="reconcile-review">
                  <div className="font-medium mb-1">Revisión manual — no se corrigen automáticamente ({plan.no_conciliadas.length})</div>
                  <ul className="space-y-1 max-h-40 overflow-auto">{plan.no_conciliadas.map((x) => (
                    <li key={x.factura_id}><span className="font-mono">{x.numero}</span> · {x.cliente_nombre} · {x.fecha} — {x.motivo}
                      {x.candidatos?.length > 0 && <span className="text-text-muted"> (estados de cuenta: {x.candidatos.map((c) => `${c.numero} ${c.fecha} ${fmtL(c.saldo)}`).join("; ")})</span>}
                    </li>))}</ul>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={`Escribe ${palabra}`}
                  data-testid="reconcile-confirm" className="bg-app border border-border rounded-md px-3 py-1.5 text-sm" />
                <button onClick={apply} disabled={busy || confirm.trim().toUpperCase() !== palabra || !t.cuadra || !plan.resumen.cambios}
                  data-testid="reconcile-apply"
                  className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-3 py-1.5 text-sm flex items-center gap-2 disabled:opacity-40">
                  <CheckCircleIcon size={14} weight="bold" /> 2. Aplicar corrección
                </button>
              </div>
            </div>
          )}
          {batches.length > 0 && (
            <div className="mt-4 text-xs">
              <div className="font-medium mb-1">Lotes aplicados</div>
              {batches.map((b) => (
                <div key={b.id} className="flex items-center gap-2 border-t border-border/50 py-1.5">
                  <span>{fmtDateTime(b.created_at)} · {b.created_by} · {b.resumen?.cambios} documentos · {b.estado}</span>
                  {b.estado === "aplicado" && (
                    <button onClick={() => revert(b.id)} disabled={busy} data-testid={`reconcile-revert-${b.id}`}
                      className="ml-auto text-agro-yellow hover:underline flex items-center gap-1">
                      <ArrowCounterClockwiseIcon size={12} /> Revertir
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
